import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ProductCard } from "./ProductCard";
import { SegmentHero, CatalogueGroups } from "./ProductParts";
import { Reveal, Eyebrow, SectionTitle } from "./Reveal";
import { PRODUCT_SEGMENTS, FEATURED_PRODUCTS, waLink } from "./data";
import { WhatsAppIcon } from "./WhatsAppIcon";

export default function Products() {
  const [active, setActive] = useState(PRODUCT_SEGMENTS[0].key);
  const seg = PRODUCT_SEGMENTS.find((s) => s.key === active);

  return (
    <section id="products" className="relative bg-ink py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal className="mb-10 flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <Eyebrow>Product Catalogue</Eyebrow>
            <SectionTitle>
              The Official <span className="text-edge">Hercules Fitness</span> Range
            </SectionTitle>
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-ash">
              The complete Hercules Fitness catalogue — every series and product
              available to see, order and install through Lift Edge, Siliguri.
              Tell us your space and budget, and we&rsquo;ll help you build the
              right gym around it.
            </p>
          </div>
          <a
            href={waLink(
              "Hi Lift Edge team, I want to build a gym within my budget. Please guide me with the right Hercules Fitness equipment."
            )}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="products-budget-cta"
            className="clip-corner-sm inline-flex items-center gap-2 border border-edge/60 bg-edge/10 px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-edge"
          >
            <WhatsAppIcon className="h-4 w-4" />
            Build My Gym Within My Budget
          </a>
        </Reveal>

        <Reveal delay={0.05}>
          <div
            className="mb-10 flex flex-wrap gap-px border border-line bg-line"
            role="tablist"
            aria-label="Product segments"
          >
            {PRODUCT_SEGMENTS.map((s) => (
              <button
                key={s.key}
                role="tab"
                aria-selected={active === s.key}
                onClick={() => setActive(s.key)}
                data-testid={`product-tab-${s.key}`}
                className={`flex-1 whitespace-nowrap px-5 py-4 font-display text-sm font-bold uppercase tracking-wider transition-colors duration-200 sm:text-base ${
                  active === s.key
                    ? "bg-edge text-white"
                    : "bg-coal text-ash hover:bg-card hover:text-white"
                }`}
              >
                {s.title}
              </button>
            ))}
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={seg.key}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="grid gap-6 lg:grid-cols-3"
          >
            <SegmentHero seg={seg} />

            <div className="lg:col-span-2">
              <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.28em] text-dim">
                Featured Models
              </p>
              <div className="mb-10 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {FEATURED_PRODUCTS[seg.key].map((p) => (
                  <ProductCard key={p.name} p={p} />
                ))}
              </div>
              <CatalogueGroups seg={seg} />
              <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.2em] text-dim">
                Product images & specifications — official Hercules Fitness catalogue. No fixed prices — every gym is planned around your budget.
              </p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
